<template>
  <div id="content">

    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col-md-12 left">
          <div class="title">
            {{ $t('sidebar.account') }}
          </div>
        </div>
      </div>
    </div>

    <div class="content col-md-12">
      <p v-if="!ready">{{ $t('message.loading') }}</p>

      <template v-else>
        <section class="col-md-12 no-padding">
          <section-header
            :title="$t('message.cleardata')"
            fontweight="bold"
          />
          <div class="row">
            <div class="col-md-12">
              <span>
                {{ $t("message.removedatainthisstoreforall") }}
              </span>
              <a
                href="https://www.treesoft.io/th/manual/setting"
                target="_blank">
                {{ $t('button.learnmore') }}
                <i class="fas fa-external-link-alt"/>
              </a>
            </div>
            <div class="col-md-12 mt-2">
              <button
                class="btn btn-danger"
                @click="openClearDataModal"
              >
                {{ $t("button.cleardata") }}
              </button>
            </div>
          </div>
        </section>

        <section class="col-md-12 no-padding mt-4">
          <section-header
            :title="$t('message.deactivateaccount')"
            fontweight="bold"
          />
          <div class="row">
            <div class="col-md-12">
              <span>
                {{ $t('message.deactivateaccountdetail') }}
              </span>
              <a
                href="https://www.treesoft.io/th/manual/setting"
                target="_blank">
                {{ $t('button.learnmore') }}
                <i class="fas fa-external-link-alt"/>
              </a>
            </div>
            <div class="col-md-12 mt-2">
              <button
                class="btn btn-danger"
                @click="openDeactivateAccountModal"
              >
                {{ $t('button.deactivateaccount') }}
              </button>
            </div>
          </div>
        </section>
      </template>
    </div>

    <div
      id="clearDataModal"
      class="modal"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
      data-keyboard="false"
      data-backdrop="static"
    >
      <div
        class="modal-dialog "
        role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('message.areyousuretodeletethis') }}</h5>
          </div>
          <div class="modal-body">
            <div
              v-if="!isModalClearDataReady"
              class="col-md-12 no-padding">
              {{ $t('message.loading') }}
            </div>
            <template v-else>
              <template v-if="filterApps.length === 0">
                {{ $t('apps.settings.message.thedatacannotbeclearedatthistime') }}
              </template>
              <template v-else>
                <ModuleTransactionCount
                  v-for="(app, index) in filterApps"
                  :key="index"
                  :appId="app.app_id"
                  :list="app.modules"
                />
                <TypeToConfirmDelete
                  :isConfirmed.sync="isConfirmedClearData"
                  learnmoreLink="https://www.treesoft.io/th/manual/setting"
                />
              </template>
            </template>
          </div>
          <div
            v-if="!isLoadingClearData"
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              @click="closeClearDataModal"
            >
              {{ $t('button.cancel') }}
            </button>
            <button
              :disabled="!isConfirmedClearData"
              type="button"
              class="btn btn-danger"
              @click="handleDeleteShopData"
            >
              {{ $t('button.delete') }}
              <i class="fas fa-exclamation-triangle"/>
            </button>
          </div>
          <div
            v-else
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              disabled>
              {{ $t('button.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              disabled>
              <i class="fas fa-spinner spin"/>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      id="deactivateAccount"
      class="modal"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
      data-keyboard="false"
      data-backdrop="static"
    >
      <div
        class="modal-dialog "
        role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('message.areyousuretodeactivateaccount') }}</h5>
          </div>
          <div class="modal-body" >
            <div style="margin-left: -16px;margin-right: -16px;margin-top: -20px;margin-bottom: -20px;">
              <TypeToConfirmDelete
                :isConfirmed.sync="isConfirmedDeactivateAccount"
                code="confirm"
                learnmoreLink="https://www.treesoft.io/th/manual/setting"
              />
            </div>
          </div>
          <div
            v-if="!isLoadingDeactivateAccount"
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              @click="closeDeactivateAccountModal"
            >
              {{ $t('button.cancel') }}
            </button>
            <button
              :disabled="!isConfirmedDeactivateAccount"
              type="button"
              class="btn btn-danger"
              @click="handleDeactivateAccount"
            >
              {{ $t('button.deactivateaccount') }}
              <i class="fas fa-exclamation-triangle"/>
            </button>
          </div>
          <div
            v-else
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              disabled>
              {{ $t('button.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              disabled>
              <i class="fas fa-spinner spin"/>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ModuleTransactionCount from '@/components/custom/ModuleTransactionCount'
import TypeToConfirmDelete from '@/components/custom/TypeToConfirmDelete'
import sidebarModule from './../../sidebarModule'

export default {
  name: 'SettingAdvanceAccount',

  components: {
    ModuleTransactionCount,
    TypeToConfirmDelete,
  },

  data () {
    return {
      ready: false,
      isConfirmedClearData: false,
      isLoadingClearData: false,
      isConfirmedDeactivateAccount: false,
      isLoadingDeactivateAccount: false,
    }
  },

  computed: {
    ...mapGetters({
      moduleTransactions: 'getModuleTransactions',
      getAppInfo: 'getAppInfo',
    }),

    isModalClearDataReady () {
      return this.moduleTransactions.length > 0
    },

    filterApps () {
      return this.moduleTransactions
        .reduce((acc, cur) => {
          const filerCount = (module) => module.count > 0
          const hasAnyCountInModules = cur.modules.some(filerCount)
          if (hasAnyCountInModules) {
            acc.push({
              ...cur,
              modules: cur.modules.filter(filerCount),
            })
          }
          return acc
        }, [])
        .map(({ app_id: appId, modules }) => {
          const mapModules = modules.map(({ module_id: moduleId, count: number }) => {
            const message = `moduletransaction.${this.getAppInfo(appId).translate}.module${moduleId}`
            return this.$t(message, { number })
          })
          return {
            app_id: appId,
            modules: mapModules,
          }
        })
    },
  },

  watch: {
    ready () {
      this.showFinish(this.$Progress)
    },
  },

  created () {
    this.ready = true
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.settings.title'),
      current: {
        app_name: 'settings',
        app_id: null,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 4,
      submenuAtIdx: 0,
    })
  },

  mounted () {
    this.setModal('#clearDataModal')
    this.setModal('#deactivateAccount')

    if (this.$route.query.alert === 'clearDataSuccess') {
      this.notify(this.$vs, {
        text: this.$t('message.removeallstoredatahasbeensuccessfully'),
        color: 'danger',
        icon: 'delete',
      })
    }
  },

  methods: {
    ...mapActions([
      'getModuleTransactions',
      'clearModuleTransactions',
      'deleteShopData',
      'deactivateAccount',
    ]),
    async openClearDataModal () {
      $('#clearDataModal').modal('show')

      await this.getModuleTransactions()
      this.showFinish(this.$Progress)

      this.$router.replace({ query: { alert: undefined } })
    },

    closeClearDataModal () {
      $('#clearDataModal').modal('hide')

      this.isConfirmedClearData = false
      this.clearModuleTransactions()
    },

    async handleDeleteShopData () {
      this.isLoadingClearData = true
      await this.deleteShopData()
      this.$router.replace({ query: { alert: 'clearDataSuccess' } })
      location.reload()
      this.isLoadingClearData = false
      this.showFinish(this.$Progress)

      this.closeClearDataModal()
      this.notify(this.$vs, {
        text: this.$t('message.removeallstoredatahasbeensuccessfully'),
        color: 'success',
        icon: 'delete',
      })

      setTimeout(() => {
        location.reload()
      }, 2000)
    },

    async openDeactivateAccountModal () {
      $('#deactivateAccount').modal('show')
    },

    closeDeactivateAccountModal () {
      $('#deactivateAccount').modal('hide')
      this.isConfirmedDeactivateAccount = false
    },

    async handleDeactivateAccount () {
      this.isLoadingDeactivateAccount = true
      await this.deactivateAccount()
      this.isLoadingDeactivateAccount = false
      this.closeDeactivateAccountModal()
      this.showFinish(this.$Progress)

      this.notify(this.$vs, {
        text: this.$t('message.deactivateaccountsuccess'),
        color: 'success',
        icon: 'check',
      })

      setTimeout(() => {
        location.reload()
      }, 3000)
    },
  },
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/apps/settings/advance/All.vue